import { api } from "../../../../../utility/AxiosInceptor";
import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CustomerInvoice from "./customer_invoice/customer_invoice";

const Invoices_Downloads = () => {
  // =========================================================================
  // STATE DECLARATIONS
  // =========================================================================
  const [apiData, setApiData] = useState([]); // Stores the main API response data
  const [roleData, setRoleData] = useState([]); // Stores user role data for dropdown
  const [model, setModel] = useState(""); // Selected billing model (Prepaid/Postpaid/ACP)
  const [user, setUser] = useState([]); // Selected user(s) from multi-select
  const [dateFrom, setDateFrom] = useState(""); // Start date filter
  const [dateTo, setDateTo] = useState(""); // End date filter
  const [invoicesData, setInvoicesData] = useState([]); // Processed invoices data for display
  const [invoicesByCustomer, setInvoicesByCustomer] = useState({}); // Invoices grouped by customer
  const [userId, setUserId] = useState(null); // Selected customer ID for invoice generation
  const [showInvoice, setShowInvoice] = useState(false); // Controls invoice modal visibility

  // Billing model options for dropdown
  const Billing_models = [
    { name: "Prepaid", value: "Prepaid" },
    { name: "Postpaid", value: "Postpaid" },
    { name: "ACP", value: "ACP" },
  ];

  // Get user data from localStorage for service provider filtering
  const serviceProvider = localStorage.getItem("userData");
  const userData = JSON.parse(serviceProvider);

  // =========================================================================
  // USE EFFECT HOOKS
  // =========================================================================

  // Fetch role data on component mount for user dropdown
  useEffect(() => {
    const roleData = async () => {
      try {
        const response = await api.get(
          `/api/web/role/getLabelRole?serviceProvider=${userData?.company}`
        );
        const data = response?.data?.data;
        setRoleData(data);
      } catch (error) {
        // Error handling can be added here if needed
      }
    };
    roleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch invoice data when filters change (date range, user, model)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Format dates for API request
        const formattedDateFrom = dateFrom
          ? new Date(dateFrom).toISOString().slice(0, 19)
          : "";
        const formattedDateTo = dateTo
          ? new Date(dateTo).toISOString().slice(0, 19)
          : "";

        // Prepare request payload with filter criteria
        const requestData = {
          startDate: formattedDateFrom,
          endDate: formattedDateTo,
          role: user,
          billingModel: model,
        };

        // API call to fetch invoices based on filters
        const response = await api.post(
          `/api/web/invoicesDownloads/invoices`,
          requestData
        );
        const data = response?.data;

        // Successful response handling
        if (response?.status === 200 || response?.status === 201) {
          setApiData(data);
          setInvoicesData(invoicesByCustomer);
        }
      } catch (error) {
        // Display error message if API call fails
        toast.error(error?.response?.data?.error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, user, model]); // Re-run when any filter changes

  // Process API data to group invoices by customer
  useEffect(() => {
    const invoicesByCustomer = [];

    // Loop through the apiData to organize invoices by customer
    apiData.forEach((item) => {
      item.customers.forEach((customer) => {
        const customerId = customer.customer._id;
        const invoices = customer.invoices;

        // Check if customerId already exists in invoicesByCustomer
        const existingCustomerIndex = invoicesByCustomer.findIndex(
          (entry) => entry.customerid === customerId
        );

        if (existingCustomerIndex !== -1) {
          // If customer exists, append invoices to existing entry
          invoicesByCustomer[existingCustomerIndex].invoices.push(...invoices);
        } else {
          // If new customer, create a new entry
          invoicesByCustomer.push({
            customerid: customerId,
            invoices: invoices,
          });
        }
      });
    });

    setInvoicesByCustomer(invoicesByCustomer);
  }, [apiData]); // Re-process when API data changes

  // =========================================================================
  // EVENT HANDLERS
  // =========================================================================

  // Handle download button click for a specific row
  const handleDownload = async (rowData) => {
    // Show the invoice modal
    setShowInvoice(true);

    // Extract and flatten invoice data from the selected row
    const invoiceData = [];
    rowData.customers.forEach((customer) => {
      if (customer.invoices && customer.invoices.length > 0) {
        invoiceData.push(...customer.invoices);
      }
    });

    // Set the extracted invoice data for display
    setInvoicesData(invoiceData);

    // Get the user ID from the first customer in the row
    const userId = rowData.customers[0]?.customer?._id;
    setUserId(userId);

    // Set the specific invoices data for the customer invoice component
    setInvoicesData(invoiceData);
  };

  // =========================================================================
  // RENDER COMPONENT
  // =========================================================================
  return (
    <>
      <ToastContainer />
      <Card className="pl-0 pr-0">
        {/* Page Header */}
        <div>
          <h1>Invoices Downloads</h1>
        </div>

        {/* Filter Section Card */}
        <Card style={{ height: "18rem" }}>
          {/* Billing Model Filter */}
          <div
            className="p-field col-12 md:col-3"
            style={{ marginLeft: "7rem" }}
          >
            <label className="Label__Text">Billing Model</label>
            <br />
            <br />
            <Dropdown
              value={model}
              onChange={(e) => setModel(e.value)}
              options={Billing_models}
              optionLabel="name"
              placeholder="Select Model"
              className="w-full md:w-14rem "
            />
          </div>

          {/* User Multi-Select Filter */}
          <div
            className="p-field col-12 md:col-3"
            style={{ marginLeft: "30rem", marginTop: "-6.5rem" }}
          >
            <label className="Label__Text">Add Users</label>
            <br />
            <br />
            <MultiSelect
              value={user}
              onChange={(e) => setUser(e.value)}
              options={roleData}
              optionLabel="role"
              display="chip"
              placeholder="Select User"
              maxSelectedLabels={3}
              optionValue="_id"
              className="w-full md:w-20rem"
            />
          </div>

          {/* Date From Filter */}
          <div
            className="p-field col-12 md:col-3 "
            style={{ marginLeft: "7rem", marginTop: "2rem" }}
          >
            <label className="Label__Text">Date From</label>
            <br />
            <br />
            <Calendar value={dateFrom} onChange={(e) => setDateFrom(e.value)} />
          </div>

          {/* Date To Filter */}
          <div
            className="p-field col-12 md:col-3 "
            style={{ marginLeft: "30rem", marginTop: "-6.5rem" }}
          >
            <label className="Label__Text">Date To</label>
            <br />
            <br />
            <Calendar value={dateTo} onChange={(e) => setDateTo(e.value)} />
          </div>
        </Card>

        {/* Data Table Section */}
        <div
          className="card"
          style={{ width: "62rem", marginLeft: "5rem", marginTop: "4rem" }}
        >
          <DataTable value={apiData} style={{ width: "50rem" }}>
            {/* User Name Column */}
            <Column field="user.name" header="Users"></Column>

            {/* Invoice Count Column - Calculates total invoices per user */}
            <Column
              body={(rowData) => {
                return (
                  <>
                    {rowData.customers.reduce(
                      (total, customer) => total + customer.invoiceCount,
                      0
                    )}
                  </>
                );
              }}
              header="Counter"
            ></Column>

            {/* Action Column - Download Button */}
            <Column
              field="Action"
              body={(rowData) => {
                return (
                  <Button
                    onClick={() => handleDownload(rowData)}
                    className="bg-blue-700 pl-2 pr-2 pt-1 pb-1 border-none"
                  >
                    Downloads
                  </Button>
                );
              }}
            />
          </DataTable>
        </div>

        {/* Customer Invoice Modal - Conditionally rendered when a user is selected */}
        {userId && showInvoice && (
          <CustomerInvoice
            setShowInvoice={setShowInvoice}
            userId={userId}
            invoicesData={invoicesData}
          />
        )}
      </Card>
    </>
  );
};

export default Invoices_Downloads;
